import { useEffect, useRef, useState } from "react";
import { api, formatErr } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { UploadSimple, X, Camera, Image as ImageIcon, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const MAX_FILE_BYTES = 2 * 1024 * 1024;

export default function AppointmentPhotosDialog({ appt, open, onOpenChange, onUpdated }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState("before");
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open || !appt?.id) return;
    setLoading(true);
    api.get(`/appointments/${appt.id}/photos`)
      .then(({ data }) => setPhotos(data.photos || []))
      .catch((e) => toast.error(formatErr(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, appt?.id]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_FILE_BYTES) { toast.error("Imagen demasiado grande (máx 2 MB)"); return; }
    if (!f.type.startsWith("image/")) { toast.error("Solo imágenes (JPG/PNG)"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setUploading(true);
      try {
        const { data } = await api.post(`/appointments/${appt.id}/photos`, {
          data_url: reader.result, stage, caption,
        });
        setPhotos((arr) => [...arr, data.photo]);
        setCaption("");
        toast.success("Foto añadida al historial");
        onUpdated?.();
      } catch (err) {
        toast.error(formatErr(err.response?.data?.detail));
      } finally { setUploading(false); fileRef.current && (fileRef.current.value = ""); }
    };
    reader.readAsDataURL(f);
  };

  const removePhoto = async (pid) => {
    if (!window.confirm("¿Eliminar esta foto del historial?")) return;
    try {
      await api.delete(`/appointments/${appt.id}/photos/${pid}`);
      setPhotos((arr) => arr.filter((p) => p.id !== pid));
      toast.success("Foto eliminada");
      onUpdated?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  if (!appt) return null;

  const before = photos.filter((p) => p.stage === "before");
  const after = photos.filter((p) => p.stage === "after");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="appt-photos-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tighter flex items-center gap-2">
            <Camera weight="duotone" className="text-[var(--ag-gold)]" /> Galería · {appt.license_plate}
          </DialogTitle>
          <p className="text-xs text-gray-500">Documenta el estado del vehículo antes y después del servicio para evitar disputas con el cliente.</p>
        </DialogHeader>

        {/* Upload */}
        <div className="bg-gray-50 border border-gray-200 rounded-sm p-4 space-y-3" data-testid="photo-upload-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Etapa</Label>
              <Select value={stage} onValueChange={setStage}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="photo-stage"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="before" data-testid="photo-stage-before">📸 Antes del servicio</SelectItem>
                  <SelectItem value="after" data-testid="photo-stage-after">✨ Después del servicio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] uppercase tracking-wider">Comentario (opcional)</Label>
              <Input value={caption} onChange={(e)=>setCaption(e.target.value)}
                placeholder="Ej. Rayón puerta delantera derecha"
                className="mt-1 rounded-sm"
                data-testid="photo-caption" maxLength={200} />
            </div>
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={handleFile}
              className="hidden" data-testid="photo-file-input" />
            <Button onClick={()=>fileRef.current?.click()} disabled={uploading}
              className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm w-full sm:w-auto" data-testid="photo-upload-btn">
              <UploadSimple size={14} className="mr-1.5" weight="bold" />
              {uploading ? "Subiendo…" : "Tomar / subir foto"}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8 text-sm text-gray-500">Cargando…</div>
        ) : (
          <div className="space-y-4">
            {[{ label: "Antes del servicio", icon: "📸", arr: before, key: "before" },
              { label: "Después del servicio", icon: "✨", arr: after, key: "after" }].map((sec) => (
              <div key={sec.key}>
                <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-2">{sec.icon} {sec.label} ({sec.arr.length})</div>
                {sec.arr.length === 0 ? (
                  <div className="text-xs text-[var(--ag-text-muted)] italic p-3 bg-gray-50 border border-dashed border-gray-200 rounded-sm">Sin fotos en esta etapa</div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {sec.arr.map((p) => (
                      <div key={p.id} className="relative group rounded-sm overflow-hidden border border-gray-200" data-testid={`photo-${p.id}`}>
                        <img src={p.data_url} alt={p.caption || ""} className="w-full h-32 object-cover" />
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent text-[var(--ag-text)] text-[10px] p-2">
                          {p.caption || <span className="italic opacity-70">Sin comentario</span>}
                        </div>
                        <button onClick={()=>removePhoto(p.id)}
                          className="absolute top-1 right-1 bg-white/90 text-red-600 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Eliminar"
                          data-testid={`photo-delete-${p.id}`}>
                          <Trash size={12} weight="bold" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
